import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Play, Clock, AlertCircle, CheckCircle2, Loader2, MessageSquare, Send, Activity, Copy, Check, FolderOpen, Paperclip, Square, Plus, Trash2, Tag, X, Puzzle, Monitor, Bot, Pencil, ListOrdered, Mic, MicOff, CalendarClock, ChevronUp, ChevronDown, Users, UserPlus, Mail, Shield, Link2, LayoutGrid, Grid3X3, GalleryHorizontalEnd, List } from 'lucide-react';
import { Button } from './Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import superjson from 'superjson';
import { useQueryClient } from '../lib/queryClient';
import styles from './TaskDetailModal.module.css';
import chatStyles from './TaskingBotChat.module.css';
import { TaskingBotChat } from './TaskingBotChat';
import { RichMessageContent, InlinePdfViewer } from '../helpers/RichMessageContent';
import { MessageFeedback } from './MessageFeedback';
import { PersonaSelector } from './PersonaSelector';
import { AiConfigDialog } from './AiConfigDialog';
import { useVoiceInput } from '../helpers/useVoiceInput';
import { parsePlanBlock, parseExecPlan } from '../helpers/planBlockParser';
import { PlanCard } from './PlanCard';
import { ExecPlanCard } from './ExecPlanCard';
import { useAuth } from '../helpers/useAuth';
import { useAiModels } from '../helpers/useAiModels';
import { UserAvatar } from './UserAvatar';
import { TaskingBotAvatarPortal } from './TaskingBotAvatarPortal';

// Normalize natural-language date strings to YYYY-MM-DD for <input type="date">
function toDateValue(v: string | null | undefined): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const lower = v.trim().toLowerCase();
  const now = new Date();
  if (lower === 'today' || lower === 'now') return now.toISOString().slice(0, 10);
  if (lower === 'tomorrow') { const d = new Date(now); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
  if (lower === 'yesterday') { const d = new Date(now); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }
  const parsed = new Date(v.trim());
  if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 2000) return parsed.toISOString().slice(0, 10);
  return '';
}
import { ChatAttachmentsLibrary } from './ChatAttachmentsLibrary';
import { uploadChatAttachment } from '../endpoints/chat-attachments/upload_POST.schema';
import { Image, Video, Music, FileText as FileTextIcon, Box, Download, ExternalLink } from 'lucide-react';

/** Inline browser SVG icon */
function BrowserIcon({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x="20" y="40" width="160" height="120" rx="8" fill="none" stroke="currentColor" strokeWidth="10"/>
      <line x1="20" y1="70" x2="180" y2="70" stroke="currentColor" strokeWidth="10"/>
      <circle cx="40" cy="55" r="4" fill="#ef4444"/>
      <circle cx="55" cy="55" r="4" fill="#f59e0b"/>
      <circle cx="70" cy="55" r="4" fill="#10b981"/>
      <rect x="90" y="52" width="80" height="6" rx="3" fill="currentColor" opacity="0.3"/>
    </svg>
  );
}

const MEDIA_TOKEN_RE_MODAL = /\[media:(image|video|audio|pdf|3d)\]\s+(https?:\/\/[^\s<>"')\]]+|\/[^\s<>"')\]]+|data:[^\s<>"')\]]+)/gi;

function extractMediaFromTextModal(text: string): { type: string; url: string }[] {
  const results: { type: string; url: string }[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(MEDIA_TOKEN_RE_MODAL.source, MEDIA_TOKEN_RE_MODAL.flags);
  while ((m = re.exec(text)) !== null) {
    results.push({ type: m[1].toLowerCase(), url: m[2].trim() });
  }
  return results;
}

function collectTaskAssetsModal(
  task: any,
  agentEvents: any[],
  taskMessages: any[],
): { type: string; url: string; source: string }[] {
  const seen = new Set<string>();
  const assets: { type: string; url: string; source: string }[] = [];
  const add = (items: { type: string; url: string }[], source: string) => {
    for (const item of items) {
      if (!seen.has(item.url)) {
        seen.add(item.url);
        assets.push({ ...item, source });
      }
    }
  };

  if (task?.result) {
    const text = typeof task.result === 'string' ? task.result : JSON.stringify(task.result);
    add(extractMediaFromTextModal(text), 'Result');
  }

  for (const evt of agentEvents) {
    if (evt.message) add(extractMediaFromTextModal(evt.message), evt.phase || 'Step');
  }

  if (task?.executionLog) {
    for (const entry of task.executionLog) {
      const text = typeof entry === 'string' ? entry : JSON.stringify(entry);
      add(extractMediaFromTextModal(text), 'Log');
    }
  }

  for (const msg of taskMessages) {
    if (msg.content) {
      add(extractMediaFromTextModal(msg.content), msg.role === 'assistant' ? 'TaskingBot' : 'You');
    }
  }

  return assets;
}

const MEDIA_ICON_MODAL: Record<string, React.ReactNode> = {
  image: <Image className="w-4 h-4" />,
  video: <Video className="w-4 h-4" />,
  audio: <Music className="w-4 h-4" />,
  pdf: <FileTextIcon className="w-4 h-4" />,
  '3d': <Box className="w-4 h-4" />,
};
import { MissingCredentialModal } from './MissingCredentialModal';
import { useMissingCredentialPrompt } from '../helpers/useMissingCredentialPrompt';
import { CREDENTIAL_REQUIREMENTS } from '../helpers/credentialRequirements';

interface TaskDetail {
  id: string;
  title: string;
  description?: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  startDate?: string;
  startTime?: string;
  dueDate?: string;
  dueTime?: string;
  cronExpression?: string;
  cronTimezone?: string;
  progress?: number;
  result?: any;
  error?: string;
  executionLog?: Array<{ timestamp: string; message: string; level: 'info' | 'error' | 'warn' }>;
  skillsUsed?: string[];
  participantsInvolved?: string[];
  autonomous?: Record<string, any>;
  agentMode?: 'auto' | 'cloud_browser' | 'extension' | 'desktop';
  model?: string | null;
  subtasks?: Array<{
    id: string;
    title: string;
    status: 'open' | 'in_progress' | 'completed' | 'cancelled';
    priority?: string;
    progress?: number;
    completedAt?: string;
  }>;
  runHistory?: Array<{
    runAt: string;
    status: string;
    model?: string;
    toolCalls?: number;
    summary: string;
  }>;
  pipeline?: any;
  quantum?: {
    strategyId?: string;
    strategyName?: string;
    agentMode?: string;
    probability?: number;
    toolSequence?: string[];
  };
}

interface TaskMessage {
  id: number;
  role: string;
  content: string;
  metadata?: string;
  createdAt?: string;
}

interface TaskDetailModalProps {
  taskId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExecute?: () => void;
  renderInline?: boolean;
}

function renderDetailText(value: unknown): string {
  const text =
    typeof value === 'string'
      ? value
      : value == null
        ? ''
        : JSON.stringify(value, null, 2);

  return text
    .replace(/\r\n?/g, '\n')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1: $2')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1: $2')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '- ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function looksLikeHtmlDocument(text: string): boolean {
  const trimmed = text.trim().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
}

function parseApiPayload<T>(text: string): T {
  try {
    return superjson.parse<T>(text);
  } catch {
    try {
      return JSON.parse(text) as T;
    } catch {
      const objectStart = text.indexOf('{');
      const objectEnd = text.lastIndexOf('}');
      if (objectStart >= 0 && objectEnd > objectStart) {
        const candidate = text.slice(objectStart, objectEnd + 1);
        try {
          return superjson.parse<T>(candidate);
        } catch {
          return JSON.parse(candidate) as T;
        }
      }
      throw new Error('Could not load data. Please try again.');
    }
  }
}

function toTaskDetailFromListItem(item: any): TaskDetail {
  let metadata: Record<string, any> = {};
  try {
    metadata =
      typeof item?.metadata === 'string'
        ? JSON.parse(item.metadata || '{}')
        : (item?.metadata ?? {});
  } catch {
    metadata = {};
  }

  return {
    id: String(item.id),
    title: item.title || 'Untitled Task',
    description: item.description || '',
    status: item.status || 'open',
    priority: item.priority,
    tags: (() => { try { const t = typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags; return Array.isArray(t) ? t : []; } catch { return []; } })(),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    completedAt: item.completedAt,
    startDate: item.startDate,
    startTime: item.startTime,
    dueDate: item.dueDate,
    dueTime: item.dueTime,
    cronExpression: item.cronExpression,
    cronTimezone: item.cronTimezone,
    progress: item.status === 'completed' ? 100 : (typeof metadata.progress === 'number' ? metadata.progress : 0),
    result: metadata.result ?? null,
    error: metadata.error ?? undefined,
    executionLog: Array.isArray(metadata.executionLog) ? metadata.executionLog : [],
    skillsUsed: Array.isArray(metadata.skillsUsed) ? metadata.skillsUsed : [],
    participantsInvolved: Array.isArray(metadata.participants) ? metadata.participants : [],
    autonomous: metadata.autonomous ?? undefined,
    agentMode: metadata.agentMode ?? 'auto',
    model: metadata.model ?? null,
    subtasks: Array.isArray(metadata.subtasks) ? metadata.subtasks : [],
    runHistory: Array.isArray(metadata.runHistory) ? metadata.runHistory : [],
    pipeline: metadata.pipeline ?? null,
    quantum: metadata.quantum ?? undefined,
  };
}

const PHASE_CONFIG: Record<string, { icon: string; color: string }> = {
  pending: { icon: '\u00b7', color: 'var(--muted-foreground)' },
  running: { icon: '\u25b8', color: '#3b82f6' },
  tool: { icon: '\u26a1', color: '#6366f1' },
  success: { icon: '\u2713', color: '#22c55e' },
  error: { icon: '\u2715', color: '#ef4444' },
};

// Integration credential checks removed — handled globally

export function TaskDetailModal({
  taskId,
  open,
  onOpenChange,
  onExecute,
  renderInline = false,
}: TaskDetailModalProps) {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [activeTaskId, setActiveTaskId] = useState(taskId);
  const [taskIdStack, setTaskIdStack] = useState<string[]>([]);
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep activeTaskId in sync with prop when it changes
  useEffect(() => {
    setActiveTaskId(taskId);
    setTaskIdStack([]);
  }, [taskId]);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const credentialPrompt = useMissingCredentialPrompt();
  const lastUserMessageRef = useRef<string>('');
  const [taskMessages, setTaskMessages] = useState<TaskMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [chatInput, setChatInput] = useState('');
  const [slashSuggestions, setSlashSuggestions] = useState<Array<{ cmd: string; args: string; desc: string }>>([]);
  const [chatSending, setChatSending] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [chatStatusMessage, setChatStatusMessage] = useState<string | null>(null);
  const [chatBackgroundThinking, setChatBackgroundThinking] = useState(false);
  const [taskBrowserViewer, setTaskBrowserViewer] = useState<{
    screenshot: string;
    url: string;
    title: string;
    status: string;
    sessionId: string;
    isBlobUrl?: boolean;
  } | null>(null);
  const [taskBrowserUserControl, setTaskBrowserUserControl] = useState(false);
  const [taskBrowserZoom, setTaskBrowserZoom] = useState<1 | 2 | 4>(1);
  const [taskBrowserInput, setTaskBrowserInput] = useState('');
  const taskBrowserScrollThrottle = useRef<number>(0);
  const [chatAttachmentLibraryOpen, setChatAttachmentLibraryOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<Array<{ name: string; type: string; content: string; size: number }>>([]);
  const taskFileInputRef = useRef<HTMLInputElement>(null);
  const [copiedMsgId, setCopiedMsgId] = useState<number | null>(null);
  const [confirmDeleteMsgId, setConfirmDeleteMsgId] = useState<number | null>(null);
  const [deletingMsgId, setDeletingMsgId] = useState<number | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const chatRequestIdRef = useRef(0);
  const chatAbortRef = useRef<AbortController | null>(null);
  const pendingAssistantCountRef = useRef(0);
  // Input history navigation (↑/↓ like main TaskingBot chat)
  const taskChatHistoryRef = useRef<string[]>(
    (() => { try { const s = localStorage.getItem('taskingbot-task-chat-history'); return s ? JSON.parse(s) : []; } catch { return []; } })()
  );
  const taskHistoryIdxRef = useRef(-1);
  const taskDraftRef = useRef('');
  type AgentMode = 'auto' | 'cloud_browser' | 'extension' | 'desktop';
  const [taskAgentMode, setTaskAgentMode] = useState<AgentMode>('auto');
  const [taskModel, setTaskModel] = useState<string>('');
  const { data: modelsData } = useAiModels();
  const availableModels = useMemo(() => {
    const cloud = modelsData?.cloudModels?.filter((m: any) => m.provider === 'ollama_cloud') || [];
    if (!cloud.find((m: any) => m.model === 'glm-5.1' || m.model === 'glm-5:cloud')) {
      cloud.unshift({ provider: 'ollama_cloud', model: 'glm-5.1', name: 'GLM 5.1' });
    }
    const local = modelsData?.localEnabled && modelsData.localModels
      ? modelsData.localModels.map((m: any) => ({ provider: 'ollama_local', model: m.model, name: `${m.name}${m.size ? ` (${m.size})` : ''} [Local]` }))
      : [];
    return [...cloud, ...local];
  }, [modelsData]);
  const [savingTask, setSavingTask] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [newTagInput, setNewTagInput] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    startDate: '',
    startTime: '',
    dueDate: '',
    dueTime: '',
    cronExpression: '',
    cronTimezone: '',
  });
  const taskAttachmentSessionId = activeTaskId ? `taskingbot-task:${activeTaskId}` : 'taskingbot-global';
  const currentUser = authState.type === 'authenticated' ? authState.user : null;

  // ── Participants state ──
  const [participants, setParticipants] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [isTaskOwner, setIsTaskOwner] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState<'view' | 'edit'>('edit');
  const [inviteExpiry, setInviteExpiry] = useState('7');
  const [inviteCreating, setInviteCreating] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  const fetchParticipants = useCallback(async () => {
    if (!activeTaskId) return;
    setParticipantsLoading(true);
    try {
      const res = await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', taskId: activeTaskId }),
      });
      if (res.ok) {
        const data = await res.json();
        setParticipants(data.participants || []);
        setInvitations(data.invitations || []);
        setIsTaskOwner(data.isOwner);
      }
    } catch (e) {
      console.error('[TaskDetailModal] fetchParticipants error:', e);
    } finally {
      setParticipantsLoading(false);
    }
  }, [activeTaskId]);

  const handleCreateInvite = useCallback(async () => {
    if (!activeTaskId) return;
    setInviteCreating(true);
    try {
      const res = await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          taskId: activeTaskId,
          email: inviteEmail || undefined,
          permissionLevel: invitePermission,
          expiresInDays: Number(inviteExpiry),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setInviteUrl(data.inviteUrl);
        fetchParticipants();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create invitation');
      }
    } catch (e: any) {
      alert(e.message || 'Failed to create invitation');
    } finally {
      setInviteCreating(false);
    }
  }, [activeTaskId, inviteEmail, invitePermission, inviteExpiry, fetchParticipants]);

  const handleRemoveParticipant = useCallback(async (userId: number) => {
    if (!activeTaskId || !confirm('Remove this participant?')) return;
    try {
      await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove', taskId: activeTaskId, userId }),
      });
      fetchParticipants();
    } catch (e) {
      console.error('[TaskDetailModal] removeParticipant error:', e);
    }
  }, [activeTaskId, fetchParticipants]);

  const handleRevokeInvitation = useCallback(async (invitationId: string) => {
    if (!activeTaskId || !confirm('Revoke this invitation?')) return;
    try {
      await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'revoke', taskId: activeTaskId, invitationId }),
      });
      fetchParticipants();
    } catch (e) {
      console.error('[TaskDetailModal] revokeInvitation error:', e);
    }
  }, [activeTaskId, fetchParticipants]);

  const buildAttachmentInsertText = useCallback((attachment: {
    fileName: string;
    shareToken?: string | null;
    dataUrl?: string;
  }) => {
    const sharedUrl = attachment.shareToken
      ? `${window.location.origin}/_api/chat-attachments/shared?token=${attachment.shareToken}`
      : null;
    if (sharedUrl) return `[attachment:${attachment.fileName}] ${sharedUrl}`;
    if (attachment.dataUrl && attachment.dataUrl.length <= 5000) {
      return `[attachment:${attachment.fileName}] ${attachment.dataUrl}`;
    }
    return `[attachment:${attachment.fileName}]`;
  }, []);

  const appendChatInput = useCallback((text: string) => {
    setChatInput((prev) => (prev.trim() ? `${prev}\n${text}` : text));
  }, []);

  const handleTaskAttachmentSelect = useCallback((selected: any) => {
    appendChatInput(buildAttachmentInsertText(selected));
    setChatAttachmentLibraryOpen(false);
  }, [appendChatInput, buildAttachmentInsertText]);

  const handleChatPaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items || !activeTaskId) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (!file) continue;
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl?.startsWith('data:')) {
          setPendingAttachments((prev) => [...prev, {
            name: file.name || 'pasted-image.png',
            type: file.type || 'image/png',
            content: dataUrl,
            size: file.size,
          }]);
        }
      };
      reader.readAsDataURL(file);
    }
  }, [activeTaskId]);

  const handleTaskFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) {
          setPendingAttachments((prev) => [...prev, {
            name: file.name,
            type: file.type,
            content: dataUrl,
            size: file.size,
          }]);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, []);

  const removePendingAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCopyMessage = useCallback(async (msgId: number, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMsgId(msgId);
      setTimeout(() => setCopiedMsgId(null), 2000);
    } catch {}
  }, []);

  const handleDeleteMessage = useCallback(async (msgId: number) => {
    setDeletingMsgId(msgId);
    try {
      const res = await fetch('/_api/taskingbot/task-messages', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: msgId, taskId: activeTaskId }),
      });
      if (res.ok) {
        setTaskMessages((prev) => prev.filter((m) => m.id !== msgId));
      }
    } catch {}
    setDeletingMsgId(null);
    setConfirmDeleteMsgId(null);
  }, [activeTaskId]);

  const handleToggleSubtaskStatus = useCallback(async (subtaskId: string, currentStatus: string) => {
    const nextStatus =
      currentStatus === 'open' ? 'in_progress' :
      currentStatus === 'in_progress' ? 'completed' :
      'open';
    try {
      await fetch('/_api/taskingbot/tasks', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ id: subtaskId, status: nextStatus }),
      });
      // Update local state
      setTask((prev) => {
        if (!prev?.subtasks) return prev;
        return {
          ...prev,
          subtasks: prev.subtasks.map((st) =>
            st.id === subtaskId
              ? { ...st, status: nextStatus as any, completedAt: nextStatus === 'completed' ? new Date().toISOString() : undefined }
              : st
          ),
        };
      });
    } catch (err) {
      console.error('Failed to update subtask:', err);
    }
  }, []);

  // Voice input
  const {
    isListening,
    transcript: _voiceTranscript,
    isSupported: isVoiceSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useVoiceInput({
    onAutoSend: (message: string) => {
      const combined = chatInput ? `${chatInput} ${message}` : message;
      setChatInput(combined);
      resetTranscript();
      pendingSendRef.current = combined;
    },
  });

  const handleVoiceToggle = () => {
    if (isListening) stopListening();
    else { resetTranscript(); startListening(); }
  };

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) setPendingAttachments((prev) => [...prev, { name: file.name, type: file.type, content: dataUrl, size: file.size }]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Auto-resize chat textarea (matches main TaskingBot chat)
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
      chatInputRef.current.style.height = Math.min(chatInputRef.current.scrollHeight, 160) + 'px';
    }
  }, [chatInput]);

  useEffect(() => {
    const assistantCount = taskMessages.filter((msg) => msg.role === 'assistant').length;
    if (chatStatusMessage && assistantCount > pendingAssistantCountRef.current) {
      setChatStatusMessage(null);
      setChatBackgroundThinking(false);
    }
  }, [chatStatusMessage, taskMessages]);

  useEffect(() => {
    if ((open || renderInline) && activeTaskId) {
      fetchTaskDetails();
      fetchTaskMessages();
    }
  }, [open, renderInline, activeTaskId]);

  // Sync agent mode from task metadata when task loads
  useEffect(() => {
    if (task?.agentMode && task.agentMode !== taskAgentMode) {
      setTaskAgentMode(task.agentMode);
    }
  }, [task?.agentMode]);

  // Sync model from task metadata when task loads
  useEffect(() => {
    const m = task?.model || '';
    if (m !== taskModel) setTaskModel(m);
  }, [task?.model]);

  // Auto-poll task details + messages when task is running (2s for real-time feel)
  // Also refetch immediately when tab regains focus so user never sees stale data
  useEffect(() => {
    if (!task || !activeTaskId || (!open && !renderInline)) return;
    if (task.status !== 'in_progress') return;
    const interval = setInterval(() => {
      fetchTaskDetails(true);
      fetchTaskMessages(true);
    }, 2000);
    const onVisible = () => {
      if (!document.hidden) {
        fetchTaskDetails(true);
        fetchTaskMessages(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [task?.status, activeTaskId, open, renderInline]);

  useEffect(() => {
    if (!task) return;
    setScheduleForm({
      startDate: toDateValue(task.startDate),
      startTime: task.startTime || '',
      dueDate: toDateValue(task.dueDate),
      dueTime: task.dueTime || '',
      cronExpression: task.cronExpression || '',
      cronTimezone: task.cronTimezone || '',
    });
  }, [task]);

  const initialLoadDone = useRef(false);

  async function fetchTaskDetails(silent = false) {
    if (!silent) { setLoading(true); }
    setError(null);
    try {
      const res = await fetch(`/_api/taskingbot/tasks/detail?taskId=${activeTaskId}`, { credentials: 'include' });
      const text = await res.text();

      if (!looksLikeHtmlDocument(text)) {
        const data = parseApiPayload<unknown>(text);
        if (
          res.ok &&
          data &&
          typeof data === 'object' &&
          'id' in (data as Record<string, unknown>)
        ) {
          setTask(toTaskDetailFromListItem(data));
          initialLoadDone.current = true;
          return;
        }
        const apiError =
          data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)
            ? String((data as Record<string, unknown>).error || 'Failed to load task')
            : 'Failed to load task';
        if (!res.ok) {
          setError(apiError);
          if (res.status === 404) setTask(null);
          return;
        }
      }

      const fallbackRes = await fetch('/_api/taskingbot/tasks?limit=100', { credentials: 'include' });
      const fallbackText = await fallbackRes.text();
      if (looksLikeHtmlDocument(fallbackText)) {
        throw new Error('Could not load task details. Please try again.');
      }
      const fallbackData = parseApiPayload<{ tasks?: any[]; error?: string }>(fallbackText);
      if (!fallbackRes.ok) throw new Error(fallbackData?.error || 'Failed to load task');

      const listTask = Array.isArray(fallbackData?.tasks)
        ? fallbackData.tasks.find((t) => String(t?.id) === String(activeTaskId))
        : null;
      if (listTask) {
        setTask(toTaskDetailFromListItem(listTask));
      } else {
        setError('Task not found.');
        setTask(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      if (!silent) { setLoading(false); }
    }
  }

  async function fetchTaskMessages(silent = false) {
    if (!silent) { setMessagesLoading(true); }
    try {
      const res = await fetch(`/_api/taskingbot/task-messages?taskId=${activeTaskId}`, { credentials: 'include' });
      const text = await res.text();
      if (looksLikeHtmlDocument(text)) return;
      const data = parseApiPayload<{ messages?: TaskMessage[] }>(text);
      if (res.ok && Array.isArray(data?.messages)) {
        setTaskMessages(data.messages);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch {
      /* non-blocking */
    } finally {
      if (!silent) { setMessagesLoading(false); }
    }
  }

  const handleCredentialSaved = useCallback(
    (_integration: string) => {
      const lastMsg = lastUserMessageRef.current;
      if (lastMsg) {
        setTimeout(() => {
          setChatInput(lastMsg);
          // Auto-retry by re-sending after a brief delay
          setTimeout(() => {
            const textarea = chatInputRef.current;
            if (textarea) {
              const event = new KeyboardEvent('keydown', { key: 'Enter' });
              textarea.dispatchEvent(event);
            }
          }, 100);
        }, 500);
      }
    },
    [],
  );

  const detectCredentialMarkers = useCallback(async (text: string) => {
    const credMarkerMatch = text.match(/\[CRED_REQUIRED:([\s\S]*?)\]/);
    if (!credMarkerMatch) return;
    try {
      const parsed = JSON.parse(credMarkerMatch[1]);
      const integrationKey = parsed.integration;
      const predefined = CREDENTIAL_REQUIREMENTS[integrationKey];

      if (parsed.fieldDefs && parsed.fieldDefs.length > 0) {
        credentialPrompt.openWithFields({
          integration: integrationKey,
          integrationLabel:
            parsed.label ||
            predefined?.label ||
            integrationKey.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          fields: parsed.fieldDefs.map((f: any) => ({
            key: f.key,
            label: f.label,
            type: f.type === 'url' ? 'text' : f.type || 'password',
            placeholder: f.placeholder || `Enter ${f.label}`,
          })),
          message: `${parsed.label || predefined?.label || integrationKey} credentials needed`,
        });
      } else if (predefined) {
        credentialPrompt.handleError({
          type: 'MISSING_CREDENTIALS',
          integration: integrationKey,
          fields: parsed.fields || predefined.fields.map((f: any) => f.key),
          message: `${predefined.label} credentials needed`,
        });
      } else {
        try {
          const ciRes = await fetch('/_api/taskingbot/custom-integrations', { credentials: 'include' });
          if (ciRes.ok) {
            const ciData = superjson.parse<any>(await ciRes.text());
            const customInt = ciData?.integrations?.find((ci: any) => ci.name === integrationKey);
            if (customInt && customInt.fields?.length > 0) {
              credentialPrompt.openWithFields({
                integration: integrationKey,
                integrationLabel: customInt.label || integrationKey,
                fields: customInt.fields.map((f: any) => ({
                  key: f.key,
                  label: f.label,
                  type: f.type === 'url' ? 'text' : f.type,
                  placeholder: f.placeholder || `Enter ${f.label}`,
                })),
              });
            }
          }
        } catch {}
      }
    } catch {}
  }, [credentialPrompt]);

  const handleSendMessage = useCallback(async () => {
    let msg = chatInput.trim();
    if ((!msg && pendingAttachments.length === 0) || chatSending || !activeTaskId) return;

    // Normalize slash commands — same as main TaskingBot chat
    if (msg.startsWith('/image ')) msg = `Generate an image with this prompt: ${msg.slice(7).trim()}`;
    else if (msg.startsWith('/video ')) msg = `Generate a short AI video for this prompt: ${msg.slice(7).trim()}`;
    else if (msg.startsWith('/sound ')) msg = `Generate a sound effect or music clip for this prompt: ${msg.slice(7).trim()}`;
    else if (msg.startsWith('/voice ')) msg = `Generate voice audio speaking this text: ${msg.slice(7).trim()}`;
    else if (msg.startsWith('/pdf ')) msg = `Generate a PDF document: ${msg.slice(5).trim()}`;
    else if (msg.startsWith('/plan ')) msg = `Create a detailed plan for: ${msg.slice(6).trim()}`;
    else if (msg.startsWith('/http ')) msg = `POST JSON to this URL: ${msg.slice(6).trim()}`;
    else if (msg.startsWith('/get ')) msg = `Fetch the content from ${msg.slice(5).trim()}`;
    else if (msg.startsWith('/task ')) msg = `Create a task: ${msg.slice(6).trim()}`;
    else if (msg.startsWith('/memory ')) msg = `Save this to my memory: ${msg.slice(8).trim()}`;
    else if (msg.startsWith('/create-skill ') || msg === '/create-skill') {
      const skillIdea = msg.slice(14).trim();
      msg = skillIdea
        ? `Create a new skill called "${skillIdea}". Research what it needs using web_search and web_scrape, then call create_skill with a comprehensive SKILL.md.`
        : `I want to create a new custom skill. Ask me what the skill should do, then research it and call create_skill with a comprehensive SKILL.md.`;
    } else if (msg.startsWith('/skills')) {
      msg = `Search for skills matching: ${msg.replace(/^\/skills\s*/, '').trim() || 'popular'}`;
    } else if (msg === '/skilltree') {
      msg = `Show me all my installed skills and available skill packs.`;
    } else if (msg === '/clear') {
      setTaskMessages([]);
      setChatInput('');
      return;
    } else if (msg === '/help') {
      msg = 'Show me all available tools and commands you can use.';
    }
    setSlashSuggestions([]);

    const requestId = Date.now();
    chatRequestIdRef.current = requestId;
    pendingAssistantCountRef.current = taskMessages.filter((m) => m.role === 'assistant').length;
    lastUserMessageRef.current = msg;

    // Upload pending attachments and build attachment text
    let attachmentText = '';
    if (pendingAttachments.length > 0) {
      const uploadedRefs: string[] = [];
      for (const att of pendingAttachments) {
        try {
          const uploaded = await uploadChatAttachment({
            sessionId: taskAttachmentSessionId,
            fileName: att.name,
            fileType: att.type,
            fileSize: att.size,
            dataUrl: att.content,
          });
          uploadedRefs.push(buildAttachmentInsertText(uploaded.attachment));
        } catch (err) {
          console.warn('[TaskDetailModal] Failed to upload attachment:', err);
          // Still include as inline data if upload fails
          if (att.content.startsWith('data:image/')) {
            uploadedRefs.push(`[media:image] ${att.content.slice(0, 200)}...`);
          }
        }
      }
      if (uploadedRefs.length > 0) {
        attachmentText = '\n' + uploadedRefs.join('\n');
      }
      setPendingAttachments([]);
    }

    const fullMessage = (msg + attachmentText).trim();

    // Save to input history
    const hist = taskChatHistoryRef.current;
    if (hist[0] !== msg && msg) {
      const newHist = [msg, ...hist].slice(0, 200);
      taskChatHistoryRef.current = newHist;
      try { localStorage.setItem('taskingbot-task-chat-history', JSON.stringify(newHist)); } catch {}
    }
    taskHistoryIdxRef.current = -1;
    taskDraftRef.current = '';
    setChatInput('');
    setChatSending(true);
    setStreamingResponse('');
    setChatStatusMessage(null);
    setChatBackgroundThinking(false);

    // Optimistically add user message to the list
    const tempUserMsg: TaskMessage = {
      id: Date.now(),
      role: 'user',
      content: fullMessage,
      createdAt: new Date().toISOString(),
    };
    setTaskMessages((prev) => [...prev, tempUserMsg]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    const abortCtrl = new AbortController();
    chatAbortRef.current = abortCtrl;
    // No hard abort timer — let the request complete naturally.
    // The server handles its own timeouts; killing the client connection
    // doesn't stop server-side work but does prevent us from receiving the reply.
    let backgrounded = false;
    let sawVisibleChunk = false;
    let pageHidden = document.hidden;
    const onVisChange = () => {
      pageHidden = document.hidden;
      if (!pageHidden && chatRequestIdRef.current === requestId) {
        // Tab just became visible — refresh data so user isn't staring at stale state
        void Promise.all([fetchTaskDetails(true), fetchTaskMessages(true)]);
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    const enterBackgroundMode = (statusMessage: string) => {
      if (chatRequestIdRef.current !== requestId || backgrounded || sawVisibleChunk) return;
      backgrounded = true;
      setChatSending(false);
      setStreamingResponse('');
      setChatStatusMessage(statusMessage);
      setChatBackgroundThinking(true);
      void Promise.all([fetchTaskDetails(true), fetchTaskMessages(true)]);
    };
    const softTimeout = window.setTimeout(() => {
      enterBackgroundMode(
        'Still working on it. Replies will show up here.',
      );
    }, 15000);
    try {
      const res = await fetch('/_api/taskingbot/task-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ taskId: activeTaskId, message: fullMessage }),
        signal: abortCtrl.signal,
      });

      if (!res.ok || !res.body) {
        window.clearTimeout(softTimeout);
        await res.text().catch(() => '');
        setStreamingResponse('');
        if (chatRequestIdRef.current === requestId) {
          setChatStatusMessage('Couldn\'t send that. Try again.');
          setChatBackgroundThinking(false);
          setChatSending(false);
        }
        return;
      }

      // Stream the response
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullText += chunk;
        const cleaned = fullText.replace(/\u200B/g, '');
        if (cleaned.trim()) {
          sawVisibleChunk = true;
          window.clearTimeout(softTimeout);
          if (chatRequestIdRef.current === requestId) {
            setChatStatusMessage(null);
            setChatBackgroundThinking(false);
          }
        }
        // Parse [BROWSER_SESSION] tag and start screenshot polling
        const sessionMatch = cleaned.match(/\[BROWSER_SESSION\]([^[]+)\[\/BROWSER_SESSION\]/);
        if (sessionMatch && sessionMatch[1] && !taskBrowserViewer?.sessionId) {
          const sid = sessionMatch[1].trim();
          setTaskBrowserViewer({ screenshot: '', url: '', title: 'Loading...', status: 'Starting...', sessionId: sid });
          // Start polling for screenshots
          const pollIv = setInterval(async () => {
            try {
              if (chatRequestIdRef.current !== requestId) { clearInterval(pollIv); return; }
              const fRes = await fetch(`/_api/taskingbot/browser-frame?sessionId=${encodeURIComponent(sid)}`, { cache: 'no-store', credentials: 'include' });
              if (fRes.ok && fRes.headers.get('Content-Type')?.includes('image')) {
                const blob = await fRes.blob();
                if (blob.size > 100) {
                  const blobUrl = URL.createObjectURL(blob);
                  setTaskBrowserViewer((prev) => prev ? {
                    ...prev,
                    screenshot: blobUrl,
                    isBlobUrl: true,
                    url: fRes.headers.get('X-Frame-Url') || prev.url,
                    title: decodeURIComponent(fRes.headers.get('X-Frame-Title') || '') || prev.title,
                    status: decodeURIComponent(fRes.headers.get('X-Frame-Status') || '') || prev.status,
                  } : prev);
                  // Auto-detect user control from header
                  const ctrl = fRes.headers.get('X-Browser-Control');
                  if (ctrl === 'user') setTaskBrowserUserControl(true);
                  else if (ctrl === 'bot') setTaskBrowserUserControl(false);
                }
              } else if (fRes.status === 204) {
                // Session ended
                clearInterval(pollIv);
              }
            } catch {}
          }, 800);
        }
        // Parse [BROWSER_STATUS] for status updates
        const statusMatch = cleaned.match(/\[BROWSER_STATUS\]([^[]*)\[\/BROWSER_STATUS\]/g);
        if (statusMatch) {
          const lastStatus = statusMatch[statusMatch.length - 1].replace(/\[\/?\w+\]/g, '').trim();
          setTaskBrowserViewer((prev) => prev ? { ...prev, status: lastStatus } : prev);
          if (/waiting for you|captcha|verify/i.test(lastStatus)) {
            setTaskBrowserUserControl(true);
          }
        }
        if (chatRequestIdRef.current === requestId) {
          // Strip browser/action tags from displayed text
          const displayText = cleaned
            .replace(/\[BROWSER_SESSION\][^[]*\[\/BROWSER_SESSION\]\n?/g, '')
            .replace(/\[BROWSER_STATUS\][^[]*\[\/BROWSER_STATUS\]\n?/g, '')
            .replace(/\[ACTIONS\][\s\S]*?\[\/ACTIONS\]\n?/g, '')
            .replace(/\[DESKTOP_ACTIONS\][\s\S]*?\[\/DESKTOP_ACTIONS\]\n?/g, '')
            .replace(/<thinking>[\s\S]*?<\/thinking>\n?/gi, '')
            .replace(/\[PIPELINE_STAGE:[^\]]*\][^\n]*\n?/g, '');
          setStreamingResponse(displayText);
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }

      // Replace streaming with final message
      window.clearTimeout(softTimeout);
      setStreamingResponse('');
      const cleanedText = fullText
        .replace(/\u200B/g, '')
        .replace(/\n?\[CRED_REQUIRED:[^\]]*\]\n?/g, '')
        .trim();
      if (cleanedText) {
        setTaskMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: 'assistant',
            content: cleanedText,
            createdAt: new Date().toISOString(),
          },
        ]);
      }
      // Detect credential markers in full response
      await detectCredentialMarkers(fullText);
      await Promise.all([fetchTaskDetails(true), fetchTaskMessages(true)]);
      if (chatRequestIdRef.current === requestId) {
        setChatStatusMessage(null);
        setChatBackgroundThinking(false);
      }
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      window.clearTimeout(softTimeout);
      setStreamingResponse('');
      const isTimeout = err instanceof DOMException && err.name === 'AbortError';
      if (chatRequestIdRef.current === requestId) {
        setChatStatusMessage(
          isTimeout
            ? 'Still thinking... replies will show up here.'
            : 'Something went wrong. Try again.',
        );
        setChatBackgroundThinking(isTimeout);
        setChatSending(false);
      }
      await Promise.all([fetchTaskDetails(true), fetchTaskMessages(true)]);
    } finally {
      window.clearTimeout(softTimeout);
      document.removeEventListener('visibilitychange', onVisChange);
      chatAbortRef.current = null;
      if (chatRequestIdRef.current === requestId && !backgrounded) {
        setChatSending(false);
      }
      chatInputRef.current?.focus();
    }
  }, [chatInput, chatSending, activeTaskId, taskMessages, fetchTaskDetails, fetchTaskMessages, detectCredentialMarkers, pendingAttachments, taskAttachmentSessionId, buildAttachmentInsertText]);

  const stopTaskGeneration = useCallback(() => {
    if (chatAbortRef.current) {
      chatAbortRef.current.abort();
      chatAbortRef.current = null;
    }
    setChatSending(false);
    setStreamingResponse('');
    setChatStatusMessage(null);
    setChatBackgroundThinking(false);
  }, []);

  // Queue a message to send — sets input and triggers send on next render
  const pendingSendRef = useRef<string | null>(null);
  const sendMessageText = useCallback((text: string) => {
    pendingSendRef.current = text;
    setChatInput(text);
  }, []);

  // Fire queued send when chatInput updates
  useEffect(() => {
    if (pendingSendRef.current && chatInput === pendingSendRef.current) {
      pendingSendRef.current = null;
      handleSendMessage();
    }
  }, [chatInput, handleSendMessage]);

  // Enhance image — calls the actual enhance API endpoint (identical to main chat)
  const [processingAsset, setProcessingAsset] = useState<string | null>(null);
  type AssetViewMode = 'small-thumbs' | 'large-thumbs' | 'tiled' | 'details';
  const [assetViewMode, setAssetViewMode] = useState<AssetViewMode>(() => {
    try { return (localStorage.getItem('task-asset-view-mode') as AssetViewMode) || 'large-thumbs'; } catch { return 'large-thumbs'; }
  });
  const setAndSaveAssetViewMode = useCallback((mode: AssetViewMode) => {
    setAssetViewMode(mode);
    try { localStorage.setItem('task-asset-view-mode', mode); } catch {}
  }, []);

  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const pdfMenuRef = useRef<HTMLDivElement>(null);

  // Close PDF menu on outside click
  useEffect(() => {
    if (!showPdfMenu) return;
    const handler = (e: MouseEvent) => {
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target as Node)) setShowPdfMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPdfMenu]);

  const pdfGenerateOptions = [
    { label: 'Document', cmd: '/document', desc: 'General document' },
    { label: 'Report', cmd: '/report', desc: 'Professional report' },
    { label: 'Proposal', cmd: '/proposal', desc: 'Business proposal' },
    { label: 'Statement of Work', cmd: '/sow', desc: 'SOW document' },
    { label: 'Technical Specs', cmd: '/specs', desc: 'Tech specification' },
    { label: 'Research Report', cmd: '/research', desc: 'Deep research with citations' },
  ];

  const handleTaskEnhanceImage = useCallback(async (imageUrl: string) => {
    if (!imageUrl) return;
    setProcessingAsset(imageUrl);
    try {
      const res = await fetch('/_api/taskingbot/enhance-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl, scale: 2 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.error || `Enhancement failed (${res.status})`);
      // Add to task messages so it appears in assets
      setTaskMessages((prev) => [
        ...prev,
        { id: Date.now(), role: 'assistant', content: `[media:image] ${data.url}`, createdAt: new Date().toISOString() },
      ]);
      setNotice('Image enhanced (2x upscale)');
      setTimeout(() => setNotice(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Enhancement failed');
      setTimeout(() => setError(null), 5000);
    } finally {
      setProcessingAsset(null);
    }
  }, []);

  const handleTaskRemoveBackground = useCallback(async (imageUrl: string) => {
    if (!imageUrl) return;
    setProcessingAsset(imageUrl);
    try {
      const res = await fetch('/_api/taskingbot/remove-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        const errMsg = typeof data?.error === 'string' ? data.error : data?.error ? JSON.stringify(data.error) : `Background removal failed (${res.status})`;
        throw new Error(errMsg);
      }
      // Add to task messages so it appears in assets
      setTaskMessages((prev) => [
        ...prev,
        { id: Date.now(), role: 'assistant', content: `[media:image] ${data.url}`, createdAt: new Date().toISOString() },
      ]);
      setNotice('Background removed');
      setTimeout(() => setNotice(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Background removal failed');
      setTimeout(() => setError(null), 5000);
    } finally {
      setProcessingAsset(null);
    }
  }, []);

  const handleRetryNow = async () => {
    if (!task) return;
    setExecuting(true);
    setError(null);
    try {
      const endpoints = ['/_api/taskingbot/tasks/execute', '/_api/taskingbot/tasks-execute-now'];
      for (const url of endpoints) {
        const res = await fetch(url, {
          method: 'POST',
          body: superjson.stringify({ taskId: task.id, force: true }),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (res.status === 404) continue;
        if (res.ok) {
          setTask((prev) => prev ? { ...prev, status: 'in_progress', progress: 5 } : prev);
          fetchTaskDetails(true);
          fetchTaskMessages(true);
        } else {
          const text = await res.text();
          const data = text.trim() ? parseApiPayload<{ error?: string }>(text) : null;
          setError(data?.error || 'Failed to retry task');
        }
        break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to retry task');
    } finally {
      setExecuting(false);
    }
  };

  const handleExecuteNow = async () => {
    if (!task) return;
    setExecuting(true);
    setError(null);
    try {
      const endpoints = ['/_api/taskingbot/tasks/execute', '/_api/taskingbot/tasks-execute-now'];
      let handled = false;
      for (const url of endpoints) {
        const res = await fetch(url, {
          method: 'POST',
          body: superjson.stringify({ taskId: task.id }),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (res.status === 404) continue;
        const text = await res.text();
        if (looksLikeHtmlDocument(text)) throw new Error('Something went wrong running this task.');
        let data: { error?: string } | null = null;
        try {
          data = text.trim() ? parseApiPayload<{ error?: string }>(text) : null;
        } catch (e) {
          if (!res.ok) throw e;
        }
        if (res.ok) {
          handled = true;
          // Immediately update UI to show running state — no waiting for poll
          setTask((prev) =>
            prev
              ? { ...prev, status: 'in_progress', progress: 5 }
              : prev,
          );
          // Fetch fresh data silently (polling will take over from here)
          fetchTaskDetails(true);
          fetchTaskMessages(true);
          if (onExecute) onExecute();
        } else {
          setError(data?.error || 'Failed to execute task');
          handled = true;
        }
        break;
      }
      if (!handled) setError('Task execution is temporarily unavailable.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const [pipelineRunning, setPipelineRunning] = useState(false);
  const handleRunPipeline = async () => {
    if (!task) return;
    setPipelineRunning(true);
    setError(null);
    try {
      // Trigger pipeline via task-messages with a "plan and execute" message
      const res = await fetch('/_api/taskingbot/task-messages', {
        method: 'POST',
        body: superjson.stringify({ taskId: task.id, message: 'plan and execute this task' }),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok && res.body) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let responseText = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          responseText += decoder.decode(value, { stream: true });
          setStreamingResponse(responseText
            .replace(/\[PIPELINE_STAGE:[^\]]*\][^\n]*\n?/g, '')
          );
        }
        // Refresh task data to show pipeline state
        fetchTaskDetails(true);
        fetchTaskMessages(true);
      } else {
        setError('Failed to start pipeline');
      }
    } catch (err: any) {
      setError(err.message || 'Pipeline failed');
    } finally {
      setPipelineRunning(false);
      setStreamingResponse('');
    }
  };

  const handlePatchTask = useCallback(
    async (updates: Record<string, unknown>, successMessage?: string) => {
      if (!task) return false;
      setSavingTask(true);
      setError(null);
      try {
        const res = await fetch('/_api/taskingbot/tasks', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: superjson.stringify({ id: task.id, ...updates }),
        });
        const text = await res.text();
        let payload: { error?: string } | null = null;
        if (text && !looksLikeHtmlDocument(text)) {
          try {
            payload = parseApiPayload<{ error?: string }>(text);
          } catch {
            payload = null;
          }
        }
      if (!res.ok) {
          throw new Error(payload?.error || 'Failed to update task');
        }
        await fetchTaskDetails();
        queryClient.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
        if (successMessage) setNotice(successMessage);
        return true;
      } catch (err) {
        setNotice(null);
        setError(err instanceof Error ? err.message : 'Failed to update task');
        return false;
      } finally {
        setSavingTask(false);
      }
    },
    [task],
  );

  const handleAgentModeChange = useCallback((mode: AgentMode) => {
    setTaskAgentMode(mode);
    if (task) {
      // The PATCH endpoint merges metadata — just send agentMode
      handlePatchTask({ agentMode: mode });
    }
  }, [task, handlePatchTask]);

  const handleStartEditTitle = useCallback(() => {
    if (!task) return;
    setEditTitleValue(task.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }, [task]);

  const handleSaveTitle = useCallback(async () => {
    const trimmed = editTitleValue.trim();
    if (!trimmed || !task || trimmed === task.title) {
      setEditingTitle(false);
      return;
    }
    const ok = await handlePatchTask({ title: trimmed }, 'Title updated');
    if (ok) setEditingTitle(false);
  }, [editTitleValue, task, handlePatchTask]);

  const handleDeleteTask = useCallback(async () => {
    if (!task) return;
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    setDeletingTask(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`/_api/taskingbot/tasks?id=${encodeURIComponent(task.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const text = await res.text();
      let payload: { error?: string } | null = null;
      if (text && !looksLikeHtmlDocument(text)) {
        try {
          payload = parseApiPayload<{ error?: string }>(text);
        } catch {
          payload = null;
        }
      }
      if (!res.ok) {
        throw new Error(payload?.error || 'Failed to delete task');
      }
      onOpenChange(false);
      if (onExecute) onExecute();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    } finally {
      setDeletingTask(false);
    }
  }, [task, onOpenChange, onExecute]);

  const STATUS_STYLES: Record<string, { bg: string; color: string; icon: typeof CheckCircle2 }> = {
    completed: {
      bg: 'color-mix(in srgb, #22c55e 12%, transparent)',
      color: '#22c55e',
      icon: CheckCircle2,
    },
    in_progress: {
      bg: 'color-mix(in srgb, #3b82f6 12%, transparent)',
      color: '#3b82f6',
      icon: Loader2,
    },
    open: {
      bg: 'color-mix(in srgb, var(--foreground) 8%, transparent)',
      color: 'var(--muted-foreground)',
      icon: Clock,
    },
    cancelled: {
      bg: 'color-mix(in srgb, #ef4444 12%, transparent)',
      color: '#ef4444',
      icon: AlertCircle,
    },
  };

  const statusStyle = STATUS_STYLES[task?.status || 'open'] || STATUS_STYLES.open;
  const StatusIcon = statusStyle.icon;

  const hasSchedule = task?.cronExpression || task?.dueDate || task?.startDate;
  const hasLogs = task?.executionLog && task.executionLog.length > 0;

  // Agent activity from autonomous.events
  const agentEvents: Array<{ phase: string; message: string; timestamp?: string }> = Array.isArray(
    task?.autonomous?.events,
  )
    ? task!.autonomous!.events
    : [];
  const botReplyEvent = agentEvents.find((e) => e.phase === 'success');
  const botReply = botReplyEvent?.message || null;
  const hasAgentActivity = agentEvents.length > 0;

  const isSubtaskView = taskIdStack.length > 0;
  const handleBackToParent = () => {
    const prev = [...taskIdStack];
    const parentId = prev.pop();
    setTaskIdStack(prev);
    if (parentId) setActiveTaskId(parentId);
  };
  const titleText = task?.title || 'Task Details';

  const body = (
    <>
      <div className={styles.stickyHeader}>
      {renderInline ? (
        <div className={styles.inlineHeader}>
          {isSubtaskView && (
            <button onClick={handleBackToParent} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', padding: '2px 6px', marginRight: 4, fontSize: 13 }} title="Back to parent task">
              ← Back
            </button>
          )}
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className={styles.title}
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', width: '100%', font: 'inherit', outline: 'none' }}
            />
          ) : (
            <h2 className={`${styles.title} ${styles.titleEditable}`} onClick={handleStartEditTitle} title="Click to rename">
              {titleText}
              <Pencil size={12} className={styles.titleEditIcon} />
            </h2>
          )}
        </div>
      ) : (
        <DialogHeader>
          {isSubtaskView && (
            <button onClick={handleBackToParent} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', padding: '2px 6px', marginBottom: 4, fontSize: 13, textAlign: 'left' }} title="Back to parent task">
              ← Back to parent
            </button>
          )}
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className={styles.title}
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', width: '100%', font: 'inherit', outline: 'none' }}
            />
          ) : (
            <DialogTitle className={`${styles.title} ${styles.titleEditable}`} onClick={handleStartEditTitle} title="Click to rename">
              {titleText}
              <Pencil size={12} className={styles.titleEditIcon} />
            </DialogTitle>
          )}
        </DialogHeader>
      )}
      </div>

        {loading && (
          <div className={styles.loadingState}>
            <Loader2 className={styles.spinner} />
            <p>Loading task details...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorState}>
            <AlertCircle size={20} />
            <p>{error}</p>
            <Button size="sm" variant="outline" onClick={() => fetchTaskDetails()}>
              Retry
            </Button>
          </div>
        )}

        {notice && !error && (
          <div className={styles.loadingState} style={{ color: 'var(--success, #22c55e)', padding: '16px 20px' }}>
            <CheckCircle2 size={18} />
            <p>{notice}</p>
          </div>
        )}

        {task && !loading && (
          <div className={styles.content}>
            {/* Top bar: Status + Progress */}
            <div className={styles.topBar}>
              <div
                className={styles.statusBadge}
                style={{ background: statusStyle.bg, color: statusStyle.color }}
              >
                <StatusIcon size={14} />
                <span>{task.status.replace('_', ' ')}</span>
              </div>

              {task.priority && (
                <div
                  className={styles.statusBadge}
                  style={{ background: 'var(--muted)', color: 'var(--foreground)' }}
                >
                  {task.priority}
                </div>
              )}

              {task.progress !== undefined && task.progress > 0 && (
                <div className={styles.progressBar}>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${task.progress}%`,
                        backgroundColor:
                          task.status === 'completed'
                            ? '#22c55e'
                            : task.status === 'in_progress'
                              ? '#3b82f6'
                              : 'var(--muted-foreground)',
                      }}
                    />
                  </div>
                  <span className={styles.progressText}>{task.progress}%</span>
                </div>
              )}

              {/* Actions inline */}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {task.status === 'in_progress' && (
                  <Button
                    size="sm"
                    onClick={handleRetryNow}
                    disabled={executing || savingTask || deletingTask}
                    className={styles.executeBtn}
                    style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
                  >
                    {executing ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <>
                        <Play size={14} /> Retry
                      </>
                    )}
                  </Button>
                )}
                {task.status !== 'in_progress' && task.status !== 'completed' && (
                  <>
                    <Button
                      size="sm"
                      onClick={handleExecuteNow}
                      disabled={executing || savingTask || deletingTask || pipelineRunning}
                      className={styles.executeBtn}
                    >
                      {executing ? (
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <>
                          <Play size={14} /> Run
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRunPipeline}
                      disabled={executing || savingTask || deletingTask || pipelineRunning}
                      style={{ gap: 4 }}
                    >
                      {pipelineRunning ? (
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <>
                          <ListOrdered size={14} /> Plan & Run
                        </>
                      )}
                    </Button>
                  </>
                )}
                {task.status !== 'cancelled' && task.status !== 'completed' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handlePatchTask({ status: 'cancelled' })}
                    disabled={executing || savingTask || deletingTask}
                  >
                    Stop
                  </Button>
                )}
                {(task.status === 'cancelled' || task.status === 'completed') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handlePatchTask({ status: 'open' })}
                    disabled={executing || savingTask || deletingTask}
                  >
                    Reopen
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void handleDeleteTask()}
                  disabled={executing || savingTask || deletingTask}
                >
                  {deletingTask ? 'Removing...' : 'Remove'}
                </Button>
              </div>
            </div>

            {/* Description */}
            {task.description && (
              <div className={styles.section}>
                <p className={styles.description}>{task.description}</p>
              </div>
            )}

            {/* Tags */}
            <div className={styles.section} style={{ paddingTop: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Tag size={13} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                {(task.tags || []).map((tag, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 9999,
                      background: 'var(--muted)', color: 'var(--foreground)',
                      fontSize: 12, lineHeight: '18px',
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => {
                        const next = (task.tags || []).filter((_, j) => j !== i);
                        void handlePatchTask({ tags: next } as any, 'Tag removed');
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--muted-foreground)' }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const trimmed = newTagInput.trim();
                    if (!trimmed) return;
                    const existing = task.tags || [];
                    if (existing.includes(trimmed)) { setNewTagInput(''); return; }
                    void handlePatchTask({ tags: [...existing, trimmed] } as any, 'Tag added');
                    setNewTagInput('');
                  }}
                  style={{ display: 'inline-flex' }}
                >
                  <input
                    ref={tagInputRef}
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    placeholder="Add tag..."
                    style={{
                      background: 'transparent', border: '1px solid var(--border)',
                      borderRadius: 9999, padding: '2px 8px', fontSize: 12,
                      width: 80, outline: 'none', color: 'var(--foreground)',
                    }}
                    onFocus={(e) => { e.currentTarget.style.width = '120px'; }}
                    onBlur={(e) => { if (!newTagInput) e.currentTarget.style.width = '80px'; }}
                  />
                </form>
              </div>
            </div>

            {/* ─── Tabs: Details | Chat | Assets ─── */}
            <Tabs defaultValue="details" className={styles.tabsRoot}>
              <TabsList className={styles.tabsList}>
                <TabsTrigger value="details" className={styles.tabTrigger}>
                  <Activity size={13} />
                  Details
                </TabsTrigger>
                <TabsTrigger value="chat" className={styles.tabTrigger}>
                  <MessageSquare size={13} />
                  Chat
                  {taskAgentMode !== 'auto' && (
                    <span title={taskAgentMode === 'cloud_browser' ? 'Cloud Browser' : taskAgentMode === 'extension' ? 'Extension' : 'Desktop'} style={{ marginLeft: 4, opacity: 0.7, display: 'inline-flex' }}>
                      {taskAgentMode === 'cloud_browser' ? <BrowserIcon size={10} /> : taskAgentMode === 'extension' ? <Puzzle size={10} /> : <Monitor size={10} />}
                    </span>
                  )}
                  {task?.quantum?.strategyId && (
                    <span className={styles.quantumIconDetail} title={`Quantum: ${task.quantum.strategyName || 'optimized'} (${task.quantum.probability || '?'}%)`} style={{ marginLeft: 2 }}>
                      <img src="/quantum.png" alt="Quantum" />
                    </span>
                  )}
                  {taskMessages.length > 0 && (
                    <span className={styles.tabBadge}>{taskMessages.length}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="assets" className={styles.tabTrigger}>
                  <FolderOpen size={13} />
                  Assets
                </TabsTrigger>
                <TabsTrigger value="participants" className={styles.tabTrigger} onClick={() => { if (!participants.length && !participantsLoading) fetchParticipants(); }}>
                  <Users size={13} />
                  Team
                  {participants.length > 0 && (
                    <span className={styles.tabBadge}>{participants.length}</span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* ── DETAILS TAB ── */}
              <TabsContent value="details" className={styles.tabContent}>
                {/* Bot Reply — only show when there's a reply or task is running */}
                {(botReply || task.status === 'in_progress') && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Bot Reply</h3>
                    {botReply ? (
                      <div className={styles.botReply}>
                        <div className={styles.botReplyHeader}>
                          <span className={styles.botReplyName}>TaskingBot</span>
                          {botReplyEvent?.timestamp && (
                            <span className={styles.botReplyTime}>
                              {new Date(botReplyEvent.timestamp).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div className={styles.botReplyBody}>{botReply}</div>
                      </div>
                    ) : (
                      <p className={styles.botReplyPlaceholder}>
                        Task is in progress — reply appears on completion.
                      </p>
                    )}
                  </div>
                )}

                {/* Pipeline Status */}
                {task.pipeline && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.85rem' }}>
                        {task.pipeline.stage === 'matching' ? '🔍' :
                         task.pipeline.stage === 'planning' ? '📋' :
                         task.pipeline.stage === 'confirming' ? '⚠️' :
                         task.pipeline.stage === 'executing' ? '🚀' :
                         task.pipeline.stage === 'completed' ? '✅' : '❌'}
                      </span>
                      Pipeline
                      <span className={styles.sectionSubtitle} style={{ textTransform: 'capitalize' }}>
                        {task.pipeline.stage}
                        {task.pipeline.completedSteps != null && ` — ${task.pipeline.completedSteps}/${task.pipeline.stepCount} steps`}
                      </span>
                    </h3>
                    {task.pipeline.planSummary && (
                      <p style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', margin: '4px 0 8px', lineHeight: 1.4 }}>
                        {task.pipeline.planSummary}
                      </p>
                    )}
                    {task.pipeline.matchedSkills?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                        {task.pipeline.matchedSkills.map((s: string) => (
                          <span key={s} style={{
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: 'color-mix(in srgb, var(--primary) 15%, transparent)',
                            color: 'var(--primary)',
                            fontWeight: 500,
                          }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {task.pipeline.progress != null && task.pipeline.stage === 'executing' && (
                      <div style={{ height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--foreground) 10%, transparent)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.round(task.pipeline.progress * 100)}%`, background: 'var(--primary)', borderRadius: 2, transition: 'width 300ms ease' }} />
                      </div>
                    )}
                    {task.pipeline.quantum?.enabled && (
                      <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'color-mix(in srgb, #8b5cf6 8%, transparent)', border: '1px solid color-mix(in srgb, #8b5cf6 20%, transparent)' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#8b5cf6', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span className={styles.quantumIconDetail}><img src="/quantum.png" alt="Quantum" /></span> Quantum Optimization
                        </div>
                        <div style={{ display: 'flex', gap: 12, fontSize: '0.65rem', color: 'var(--muted-foreground)' }}>
                          <span>{task.pipeline.quantum.strategies?.length || 0} qubits</span>
                          <span>{task.pipeline.quantum.entanglements || 0} entangled</span>
                          {task.pipeline.quantum.executionOrder && (
                            <span>Order: {task.pipeline.quantum.executionOrder.slice(0, 3).map((id: string) => id.slice(-5)).join(' → ')}</span>
                          )}
                        </div>
                        {task.pipeline.quantum.strategies?.map((s: any) => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <div style={{
                              width: 6, height: 6, borderRadius: '50%',
                              background: s.measured ? '#22c55e' : '#8b5cf6',
                              boxShadow: s.measured ? '0 0 4px #22c55e' : 'none',
                            }} />
                            <span style={{ fontSize: '0.6rem', color: 'var(--muted-foreground)', flex: 1 }}>
                              {s.title?.slice(0, 30) || s.id.slice(-8)}
                            </span>
                            <span style={{ fontSize: '0.6rem', color: s.measured ? '#22c55e' : '#8b5cf6' }}>
                              {s.strategy || (s.entangled > 0 ? `⟨${s.entangled}⟩` : '|ψ⟩')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {task.pipeline.stage === 'confirming' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <Button
                          size="sm"
                          onClick={async () => {
                            setPipelineRunning(true);
                            try {
                              await fetch('/_api/taskingbot/task-messages', {
                                method: 'POST',
                                body: superjson.stringify({ taskId: task.id, message: 'confirm and run it' }),
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                              });
                              fetchTaskDetails(true);
                              fetchTaskMessages(true);
                            } finally {
                              setPipelineRunning(false);
                            }
                          }}
                          disabled={pipelineRunning}
                          style={{ gap: 4 }}
                        >
                          {pipelineRunning ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
                          Confirm & Execute
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePatchTask({ status: 'open' })}
                        >
                          Cancel Plan
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Subtasks */}
                {task.subtasks && task.subtasks.length > 0 && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      Subtasks
                      <span className={styles.sectionSubtitle}>
                        {task.subtasks.filter((s) => s.status === 'completed').length}/{task.subtasks.length} completed
                      </span>
                    </h3>
                    <div className={styles.subtaskList}>
                      {task.subtasks.map((st) => {
                        const isCompleted = st.status === 'completed';
                        const isInProgress = st.status === 'in_progress';
                        const subtaskProgress = isCompleted ? 100 : (st.progress ?? (isInProgress ? 50 : 0));
                        return (
                          <div
                            key={st.id}
                            className={`${styles.subtaskItem} ${isCompleted ? styles.subtaskItemCompleted : ''}`}
                          >
                            <div className={styles.subtaskRow}>
                              <button
                                className={`${styles.subtaskCheckbox} ${isCompleted ? styles.subtaskCheckboxChecked : isInProgress ? styles.subtaskCheckboxInProgress : ''}`}
                                onClick={() => handleToggleSubtaskStatus(st.id, st.status)}
                                title={isCompleted ? 'Mark as open' : isInProgress ? 'Mark as completed' : 'Mark as in progress'}
                              >
                                {isCompleted && (
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="2 6 5 9 10 3" />
                                  </svg>
                                )}
                                {isInProgress && <span className={styles.subtaskCheckboxDash} />}
                              </button>
                              <span
                                className={`${styles.subtaskTitle} ${isCompleted ? styles.subtaskTitleCompleted : ''}`}
                                style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                                onClick={() => {
                                  setTaskIdStack((prev) => [...prev, activeTaskId]);
                                  setActiveTaskId(st.id);
                                }}
                                title="View subtask details"
                              >
                                {st.title}
                              </span>
                              {st.priority && (
                                <span className={styles.subtaskPriority}>{st.priority}</span>
                              )}
                              <span className={`${styles.subtaskStatus} ${styles[`subtaskStatus_${st.status}`] || ''}`}>
                                {st.status.replace('_', ' ')}
                              </span>
                            </div>
                            {subtaskProgress > 0 && (
                              <div className={styles.subtaskProgressBar}>
                                <div className={styles.subtaskProgressTrack}>
                                  <div
                                    className={styles.subtaskProgressFill}
                                    style={{
                                      width: `${subtaskProgress}%`,
                                      backgroundColor: isCompleted ? '#22c55e' : isInProgress ? '#3b82f6' : 'var(--muted-foreground)',
                                    }}
                                  />
                                </div>
                                <span className={styles.subtaskProgressText}>{subtaskProgress}%</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Agent Activity */}
                {hasAgentActivity && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Agent Activity</h3>
                    <div className={styles.agentTimeline}>
                      {agentEvents.map((evt, idx) => {
                        const cfg = PHASE_CONFIG[evt.phase] || PHASE_CONFIG.pending;
                        const msg =
                          evt.phase === 'success' && evt.message?.length > 280
                            ? evt.message.slice(0, 280) + '...'
                            : evt.message;
                        return (
                          <div
                            key={idx}
                            className={styles.timelineEntry}
                            style={{ borderLeftColor: cfg.color }}
                          >
                            <span className={styles.timelineIcon} style={{ color: cfg.color }}>
                              {cfg.icon}
                            </span>
                            <div className={styles.timelineContent}>
                              <span className={styles.timelinePhase}>{evt.phase}</span>
                              {evt.timestamp && (
                                <span className={styles.timelineTime}>
                                  {new Date(evt.timestamp).toLocaleTimeString()}
                                </span>
                              )}
                              {msg && <p className={styles.timelineMessage}>{msg}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Metadata footer */}
                    {task.autonomous && (
                      <div className={styles.agentMeta}>
                        {task.autonomous.lastModel && <span>Model: {task.autonomous.lastModel}</span>}
                        {task.autonomous.lastRunStatus && (
                          <span>Status: {task.autonomous.lastRunStatus}</span>
                        )}
                        {typeof task.autonomous.runCount === 'number' && (
                          <span>Runs: {task.autonomous.runCount}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Result */}
                {task.result && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Result</h3>
                    <div className={styles.resultBox}>
                      {typeof task.result === 'string' ? (
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {renderDetailText(task.result)}
                        </div>
                      ) : (
                        <pre>{JSON.stringify(task.result, null, 2)}</pre>
                      )}
                    </div>
                  </div>
                )}

                {/* Schedule */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {hasSchedule ? 'Schedule / Reschedule' : 'Schedule'}
                  </h3>
                  <div className={styles.scheduleGrid}>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Start Date</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="YYYY-MM-DD"
                          pattern="\d{4}-\d{2}-\d{2}"
                          value={scheduleForm.startDate}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, startDate: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="date"
                          value={scheduleForm.startDate}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, startDate: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ width: 28, padding: '0 2px', opacity: 0.6 }}
                          tabIndex={-1}
                        />
                      </div>
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Start Time</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="HH:MM"
                          pattern="\d{2}:\d{2}"
                          value={scheduleForm.startTime}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, startTime: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="time"
                          value={scheduleForm.startTime}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, startTime: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ width: 28, padding: '0 2px', opacity: 0.6 }}
                          tabIndex={-1}
                        />
                      </div>
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Due Date</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="YYYY-MM-DD"
                          pattern="\d{4}-\d{2}-\d{2}"
                          value={scheduleForm.dueDate}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, dueDate: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="date"
                          value={scheduleForm.dueDate}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, dueDate: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ width: 28, padding: '0 2px', opacity: 0.6 }}
                          tabIndex={-1}
                        />
                      </div>
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Due Time</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="HH:MM"
                          pattern="\d{2}:\d{2}"
                          value={scheduleForm.dueTime}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, dueTime: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="time"
                          value={scheduleForm.dueTime}
                          onChange={(e) =>
                            setScheduleForm((prev) => ({ ...prev, dueTime: e.target.value }))
                          }
                          className={styles.scheduleInput}
                          style={{ width: 28, padding: '0 2px', opacity: 0.6 }}
                          tabIndex={-1}
                        />
                      </div>
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Frequency</span>
                      <select
                        value={
                          scheduleForm.cronExpression === '' ? '' :
                          scheduleForm.cronExpression === '0 9 * * 1-5' ? 'weekdays' :
                          scheduleForm.cronExpression === '0 9 * * *' ? 'daily' :
                          scheduleForm.cronExpression === '0 9 * * 1' ? 'weekly' :
                          scheduleForm.cronExpression === '0 9 1,15 * *' ? 'biweekly' :
                          scheduleForm.cronExpression === '0 9 1 * *' ? 'monthly' :
                          scheduleForm.cronExpression === '0 9 1 */3 *' ? 'quarterly' :
                          scheduleForm.cronExpression === '0 9 1 */6 *' ? 'biannual' :
                          scheduleForm.cronExpression === '0 9 1 1 *' ? 'yearly' :
                          'custom'
                        }
                        onChange={(e) => {
                          const presets: Record<string, string> = {
                            '': '',
                            daily: '0 9 * * *',
                            weekdays: '0 9 * * 1-5',
                            weekly: '0 9 * * 1',
                            biweekly: '0 9 1,15 * *',
                            monthly: '0 9 1 * *',
                            quarterly: '0 9 1 */3 *',
                            biannual: '0 9 1 */6 *',
                            yearly: '0 9 1 1 *',
                          };
                          if (e.target.value !== 'custom' && presets[e.target.value] !== undefined) {
                            setScheduleForm((prev) => ({ ...prev, cronExpression: presets[e.target.value] }));
                          }
                        }}
                        className={styles.scheduleInput}
                      >
                        <option value="">One-time (no repeat)</option>
                        <option value="daily">Daily</option>
                        <option value="weekdays">Weekdays (Mon-Fri)</option>
                        <option value="weekly">Weekly (Monday)</option>
                        <option value="biweekly">Bi-weekly (1st & 15th)</option>
                        <option value="monthly">Monthly (1st)</option>
                        <option value="quarterly">Every 3 months</option>
                        <option value="biannual">Every 6 months</option>
                        <option value="yearly">Once a year</option>
                        <option value="custom">Custom cron</option>
                      </select>
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Cron Expression</span>
                      <input
                        type="text"
                        value={scheduleForm.cronExpression}
                        onChange={(e) =>
                          setScheduleForm((prev) => ({ ...prev, cronExpression: e.target.value }))
                        }
                        placeholder="0 9 * * 1-5"
                        className={styles.scheduleInput}
                      />
                    </label>
                    <label className={styles.scheduleItem}>
                      <span className={styles.scheduleLabel}>Timezone</span>
                      <input
                        type="text"
                        value={scheduleForm.cronTimezone}
                        onChange={(e) =>
                          setScheduleForm((prev) => ({ ...prev, cronTimezone: e.target.value }))
                        }
                        placeholder="America/Chicago"
                        className={styles.scheduleInput}
                      />
                    </label>
                  </div>
                  {/* Model picker for scheduled/autonomous execution */}
                  <label className={styles.scheduleItem} style={{ marginTop: 12 }}>
                    <span className={styles.scheduleLabel}>AI Model</span>
                    <select
                      value={taskModel}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTaskModel(val);
                        void handlePatchTask({ model: val || null });
                      }}
                      className={styles.scheduleInput}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">Default (auto-select)</option>
                      {availableModels.map((m: any) => (
                        <option key={m.model} value={m.model}>{m.name || m.model}</option>
                      ))}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setScheduleForm({
                          startDate: task.startDate || '',
                          startTime: task.startTime || '',
                          dueDate: task.dueDate || '',
                          dueTime: task.dueTime || '',
                          cronExpression: task.cronExpression || '',
                          cronTimezone: task.cronTimezone || '',
                        })
                      }
                      disabled={savingTask || deletingTask}
                    >
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        void handlePatchTask({
                          startDate: scheduleForm.startDate || null,
                          startTime: scheduleForm.startTime || null,
                          dueDate: scheduleForm.dueDate || null,
                          dueTime: scheduleForm.dueTime || null,
                          cronExpression: scheduleForm.cronExpression || null,
                          cronTimezone: scheduleForm.cronTimezone || null,
                        })
                      }
                      disabled={savingTask || deletingTask}
                    >
                      {savingTask ? 'Saving…' : hasSchedule ? 'Reschedule' : 'Schedule'}
                    </Button>
                    {hasSchedule && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void handlePatchTask({
                            startDate: null,
                            startTime: null,
                            dueDate: null,
                            dueTime: null,
                            cronExpression: null,
                            cronTimezone: null,
                          })
                        }
                        disabled={savingTask || deletingTask}
                      >
                        Clear Schedule
                      </Button>
                    )}
                  </div>
                </div>

                {/* Last Run Summary (if available) */}
                {task.autonomous?.lastRunSummary && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Last Run</h3>
                    <div className={styles.resultBox}>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderDetailText(task.autonomous.lastRunSummary)}
                      </div>
                    </div>
                    {task.autonomous?.lastRunError && (
                      <div className={`${styles.resultBox} ${styles.errorBox}`} style={{ marginTop: 8 }}>
                        <p>{task.autonomous.lastRunError}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Execution Logs */}
                {hasLogs && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Execution Log</h3>
                    <div className={styles.logContainer}>
                      {task.executionLog!.slice(-10).map((log, idx) => (
                        <div key={idx} className={`${styles.logEntry} ${styles[`log-${log.level}`]}`}>
                          <span className={styles.logTime}>
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={styles.logMessage}>{log.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Run History */}
                {task.runHistory && task.runHistory.length > 1 && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Run History ({task.runHistory.length} runs)</h3>
                    <div className={styles.logContainer}>
                      {task.runHistory.slice().reverse().map((run, idx) => (
                        <div key={idx} className={`${styles.logEntry} ${styles[`log-${run.status === 'success' ? 'info' : 'error'}`]}`}>
                          <span className={styles.logTime}>
                            {new Date(run.runAt).toLocaleString()}
                          </span>
                          <span className={styles.logMessage}>
                            {run.status === 'success' ? '\u2705' : '\u274c'} {run.model || 'unknown'} — {run.toolCalls || 0} tool calls
                          </span>
                          <div style={{ fontSize: '0.82em', opacity: 0.75, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%' }}>
                            {run.summary.length > 200 ? run.summary.slice(0, 200) + '…' : run.summary}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error */}
                {task.error && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Error</h3>
                    <div className={`${styles.resultBox} ${styles.errorBox}`}>
                      <p>{task.error}</p>
                    </div>
                  </div>
                )}

                {/* Skills Used */}
                {task.skillsUsed && task.skillsUsed.length > 0 && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Skills Used</h3>
                    <div className={styles.skillsList}>
                      {task.skillsUsed.map((skill, idx) => (
                        <span key={idx} className={styles.skillBadge}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ── CHAT TAB ── */}
              <TabsContent value="chat" className={`${styles.tabContent} ${styles.chatTabContent}`}>
                <TaskingBotChat projectId={null} taskId={activeTaskId} />
              </TabsContent>

              <TabsContent value="assets" className={styles.tabContent}>
                {(() => {
                  const assets = collectTaskAssetsModal(task, agentEvents, taskMessages);
                  const viewModes: { value: AssetViewMode; label: string; icon: React.ReactNode }[] = [
                    { value: 'small-thumbs', label: 'Small', icon: <Grid3X3 size={13} /> },
                    { value: 'large-thumbs', label: 'Large', icon: <LayoutGrid size={13} /> },
                    { value: 'tiled', label: 'Tiled', icon: <GalleryHorizontalEnd size={13} /> },
                    { value: 'details', label: 'Details', icon: <List size={13} /> },
                  ];
                  const gridStyle: React.CSSProperties = assetViewMode === 'small-thumbs'
                    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }
                    : assetViewMode === 'large-thumbs'
                    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }
                    : assetViewMode === 'tiled'
                    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }
                    : { display: 'flex', flexDirection: 'column', gap: 4 };
                  return assets.length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '2rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <FolderOpen style={{ width: 32, height: 32, margin: '0 auto 12px', opacity: 0.4 }} />
                        <p style={{ fontSize: 13, fontWeight: 600, opacity: 0.7 }}>No assets yet</p>
                        <p style={{ fontSize: 11, opacity: 0.45, marginTop: 4 }}>
                          Generated images, videos, audio, PDFs, and 3D models will appear here.
                        </p>
                        <div ref={pdfMenuRef} style={{ position: 'relative', display: 'inline-block', marginTop: 12 }}>
                          <button
                            type="button"
                            onClick={() => setShowPdfMenu((v) => !v)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                              fontSize: 12, fontWeight: 600, border: '1px solid rgba(255,255,255,0.15)',
                              borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.06)',
                              color: 'rgba(255,255,255,0.7)', transition: 'all 0.15s ease',
                            }}
                          >
                            <Plus size={13} />
                            Generate PDF
                            <ChevronDown size={11} style={{ opacity: 0.5 }} />
                          </button>
                          {showPdfMenu && (
                            <div style={{
                              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginTop: 4, zIndex: 50,
                              background: 'var(--card, #1a1a2e)', border: '1px solid rgba(255,255,255,0.12)',
                              borderRadius: 10, padding: 4, minWidth: 200,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            }}>
                              {pdfGenerateOptions.map((opt) => (
                                <button
                                  key={opt.cmd}
                                  type="button"
                                  onClick={() => {
                                    setShowPdfMenu(false);
                                    sendMessageText(`${opt.cmd} ${task.title || task.description || ''}`);
                                  }}
                                  style={{
                                    display: 'flex', flexDirection: 'column', width: '100%', padding: '8px 12px',
                                    border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                                    background: 'transparent', color: '#fff', transition: 'background 0.1s',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <span style={{ fontSize: 12, fontWeight: 600 }}>{opt.label}</span>
                                  <span style={{ fontSize: 10, opacity: 0.45, marginTop: 1 }}>{opt.desc}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5 }}>
                          {assets.length} asset{assets.length !== 1 ? 's' : ''}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div ref={pdfMenuRef} style={{ position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => setShowPdfMenu((v) => !v)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                                fontSize: 11, fontWeight: 600, border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.06)',
                                color: 'rgba(255,255,255,0.7)', transition: 'all 0.15s ease',
                              }}
                            >
                              <Plus size={12} />
                              Generate
                              <ChevronDown size={10} style={{ opacity: 0.5 }} />
                            </button>
                            {showPdfMenu && (
                              <div style={{
                                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 50,
                                background: 'var(--card, #1a1a2e)', border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 10, padding: 4, minWidth: 200,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                              }}>
                                {pdfGenerateOptions.map((opt) => (
                                  <button
                                    key={opt.cmd}
                                    type="button"
                                    onClick={() => {
                                      setShowPdfMenu(false);
                                      sendMessageText(`${opt.cmd} ${task.title || task.description || ''}`);
                                    }}
                                    style={{
                                      display: 'flex', flexDirection: 'column', width: '100%', padding: '8px 12px',
                                      border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                                      background: 'transparent', color: '#fff', transition: 'background 0.1s',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                  >
                                    <span style={{ fontSize: 12, fontWeight: 600 }}>{opt.label}</span>
                                    <span style={{ fontSize: 10, opacity: 0.45, marginTop: 1 }}>{opt.desc}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                          {viewModes.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setAndSaveAssetViewMode(opt.value)}
                              title={opt.label}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 26, height: 26, border: 'none', borderRadius: 6, cursor: 'pointer',
                                background: assetViewMode === opt.value ? 'var(--primary)' : 'transparent',
                                color: assetViewMode === opt.value ? '#fff' : 'rgba(255,255,255,0.4)',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {opt.icon}
                            </button>
                          ))}
                        </div>
                        </div>
                      </div>
                      <div style={gridStyle}>
                        {assets.map((asset, i) => {
                          // Details view: horizontal row
                          if (assetViewMode === 'details') {
                            return (
                              <div
                                key={`${asset.url}-${i}`}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                                  borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                                  background: 'rgba(255,255,255,0.03)', cursor: 'pointer',
                                }}
                              >
                                <div style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)' }}>
                                  {asset.type === 'image' ? (
                                    <img src={asset.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} loading="lazy" />
                                  ) : (
                                    <span style={{ opacity: 0.4 }}>{MEDIA_ICON_MODAL[asset.type] || <FileTextIcon style={{ width: 16, height: 16 }} />}</span>
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{asset.source}</span>
                                  <span style={{ fontSize: 10, opacity: 0.4, textTransform: 'uppercase' }}>{asset.type}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                  {asset.type === 'image' && (
                                    <>
                                      <button onClick={() => handleTaskEnhanceImage(asset.url)} title="Enhance" className={styles.assetActionBtn} disabled={!!processingAsset}>
                                        {processingAsset === asset.url ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>}
                                      </button>
                                      <button onClick={() => handleTaskRemoveBackground(asset.url)} title="Remove BG" className={styles.assetActionBtn} disabled={!!processingAsset}>
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/></svg>
                                      </button>
                                    </>
                                  )}
                                  <a href={asset.url} target="_blank" rel="noopener noreferrer" title="Open" style={{ padding: 4, opacity: 0.4 }}><ExternalLink style={{ width: 12, height: 12 }} /></a>
                                  <a href={asset.url} download title="Download" style={{ padding: 4, opacity: 0.4 }}><Download style={{ width: 12, height: 12 }} /></a>
                                </div>
                              </div>
                            );
                          }

                          // Small thumbs: compact card
                          if (assetViewMode === 'small-thumbs') {
                            return (
                              <div
                                key={`${asset.url}-${i}`}
                                style={{
                                  borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)',
                                  background: 'rgba(255,255,255,0.03)', padding: 4, display: 'flex', flexDirection: 'column', gap: 4,
                                  ...(asset.type === 'pdf' ? { gridColumn: '1 / -1' } : {}),
                                }}
                              >
                                {asset.type === 'image' ? (
                                  <div style={{ width: '100%', aspectRatio: '1', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
                                    <img src={asset.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} loading="lazy" />
                                  </div>
                                ) : asset.type === 'pdf' ? (
                                  <div style={{ minHeight: 300 }}><InlinePdfViewer url={asset.url} /></div>
                                ) : (
                                  <div style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.02)' }}>
                                    <span style={{ opacity: 0.4 }}>{MEDIA_ICON_MODAL[asset.type] || <FileTextIcon style={{ width: 24, height: 24 }} />}</span>
                                  </div>
                                )}
                                <span style={{ fontSize: 9, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 2px', opacity: 0.6 }}>{asset.source} · {asset.type}</span>
                              </div>
                            );
                          }

                          // Large thumbs & Tiled share similar structure
                          return (
                            <div
                              key={`${asset.url}-${i}`}
                              className={styles.assetCard || ''}
                              style={{
                                borderRadius: 12, overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(255,255,255,0.03)',
                                ...(asset.type === 'pdf' ? { gridColumn: '1 / -1' } : {}),
                              }}
                            >
                              {asset.type === 'image' ? (
                                <div style={{ overflow: 'hidden', ...(assetViewMode === 'tiled' ? { aspectRatio: '16/10' } : {}) }}>
                                  <img src={asset.url} alt={`Asset from ${asset.source}`} style={{ width: '100%', height: assetViewMode === 'tiled' ? '100%' : 'auto', objectFit: 'contain' }} loading="lazy" />
                                </div>
                              ) : asset.type === 'video' ? (
                                <div style={{ aspectRatio: '16/9', overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
                                  <video src={asset.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} controls preload="metadata" />
                                </div>
                              ) : asset.type === 'audio' ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                                  <audio src={asset.url} controls style={{ width: '100%' }} preload="metadata" />
                                </div>
                              ) : asset.type === 'pdf' ? (
                                <div style={{ minHeight: 400 }}>
                                  <InlinePdfViewer url={asset.url} />
                                </div>
                              ) : (
                                <div style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.02)' }}>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ opacity: 0.4 }}>{MEDIA_ICON_MODAL[asset.type] || <FileTextIcon style={{ width: 32, height: 32 }} />}</div>
                                    <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.4, marginTop: 8 }}>{asset.type}</p>
                                  </div>
                                </div>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: 'rgba(0,0,0,0.2)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                  <span style={{ opacity: 0.5 }}>{MEDIA_ICON_MODAL[asset.type]}</span>
                                  <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.source}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {asset.type === 'image' && (
                                    <>
                                      <button
                                        onClick={() => handleTaskEnhanceImage(asset.url)}
                                        title="Enhance (2x upscale)"
                                        className={styles.assetActionBtn}
                                        disabled={!!processingAsset}
                                      >
                                        {processingAsset === asset.url ? (
                                          <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} />
                                        ) : (
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => handleTaskRemoveBackground(asset.url)}
                                        title="Remove background"
                                        className={styles.assetActionBtn}
                                        disabled={!!processingAsset}
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/></svg>
                                      </button>
                                      <button
                                        onClick={() => sendMessageText(`Regenerate the previous image with a different variation. Use the same prompt/concept but create a new unique result.`)}
                                        title="Regenerate"
                                        className={styles.assetActionBtn}
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                                      </button>
                                      <button
                                        onClick={() => sendMessageText(`Edit this image with the region editor: ${asset.url}`)}
                                        title="Edit"
                                        className={styles.assetActionBtn}
                                      >
                                        <Pencil style={{ width: 11, height: 11 }} />
                                      </button>
                                    </>
                                  )}
                                  <a href={asset.url} target="_blank" rel="noopener noreferrer" title="Open in new tab" style={{ padding: 4, opacity: 0.4 }}>
                                    <ExternalLink style={{ width: 12, height: 12 }} />
                                  </a>
                                  <a href={asset.url} download title="Download" style={{ padding: 4, opacity: 0.4 }}>
                                    <Download style={{ width: 12, height: 12 }} />
                                  </a>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </TabsContent>

              {/* ── PARTICIPANTS TAB ── */}
              <TabsContent value="participants" className={styles.tabContent}>
                <div style={{ padding: '16px' }}>
                  {/* Invite button */}
                  {isTaskOwner && !showInviteForm && !inviteUrl && (
                    <button
                      onClick={() => setShowInviteForm(true)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '12px 16px', borderRadius: '10px', border: '1px dashed var(--border)',
                        background: 'transparent', color: 'var(--primary)', cursor: 'pointer',
                        fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.15s ease',
                        marginBottom: '16px',
                      }}
                    >
                      <UserPlus size={16} />
                      Invite Participant
                    </button>
                  )}

                  {/* Invite form */}
                  {showInviteForm && !inviteUrl && (
                    <div style={{
                      padding: '16px', borderRadius: '10px', border: '1px solid var(--border)',
                      background: 'var(--muted)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={14} style={{ color: 'var(--muted-foreground)' }} />
                        <input
                          type="email"
                          placeholder="Email (optional — restrict to this email)"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          style={{
                            flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)',
                            background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.8rem',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Shield size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                          <select
                            value={invitePermission}
                            onChange={(e) => setInvitePermission(e.target.value as 'view' | 'edit')}
                            style={{
                              flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)',
                              background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.8rem',
                            }}
                          >
                            <option value="view">View Only</option>
                            <option value="edit">Can Edit</option>
                          </select>
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                          <select
                            value={inviteExpiry}
                            onChange={(e) => setInviteExpiry(e.target.value)}
                            style={{
                              flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)',
                              background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.8rem',
                            }}
                          >
                            <option value="1">1 Day</option>
                            <option value="7">7 Days</option>
                            <option value="30">30 Days</option>
                            <option value="90">90 Days</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button variant="ghost" size="sm" onClick={() => { setShowInviteForm(false); setInviteEmail(''); }}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleCreateInvite} disabled={inviteCreating}>
                          {inviteCreating ? <><Loader2 size={14} className={chatStyles?.spinning || ''} /> Generating...</> : 'Generate Invite Link'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Invite success */}
                  {inviteUrl && (
                    <div style={{
                      padding: '16px', borderRadius: '10px', border: '1px solid var(--primary)',
                      background: 'color-mix(in srgb, var(--primary) 8%, var(--background))',
                      marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--primary)', fontWeight: 600, fontSize: '0.85rem' }}>
                        <Check size={16} />
                        Invitation Created
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                        <input
                          readOnly
                          value={inviteUrl}
                          style={{
                            flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)',
                            background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.75rem',
                          }}
                          onClick={(e) => (e.target as HTMLInputElement).select()}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(inviteUrl);
                            setInviteCopied(true);
                            setTimeout(() => setInviteCopied(false), 2000);
                          }}
                        >
                          {inviteCopied ? <Check size={14} /> : <Copy size={14} />}
                        </Button>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setInviteUrl(null); setShowInviteForm(false); setInviteEmail(''); }}>
                        Done
                      </Button>
                    </div>
                  )}

                  {/* Owner row */}
                  {task && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                      borderRadius: '8px', background: 'color-mix(in srgb, var(--primary) 6%, var(--background))',
                      border: '1px solid color-mix(in srgb, var(--primary) 20%, var(--border))',
                      marginBottom: '8px',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem',
                      }}>
                        {currentUser?.displayName?.[0]?.toUpperCase() || 'O'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{currentUser?.displayName || 'Owner'}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--muted-foreground)' }}>{currentUser?.email || ''}</div>
                      </div>
                      <span style={{
                        padding: '3px 10px', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
                        background: 'var(--primary)', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}>
                        Owner
                      </span>
                    </div>
                  )}

                  {/* Participants list */}
                  {participantsLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}>
                      <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : participants.length === 0 && invitations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)', fontSize: '0.8rem' }}>
                      <Users size={28} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                      <p>No participants yet</p>
                      <p style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: 4 }}>Invite team members to collaborate on this task</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {participants.map((p: any) => (
                        <div key={p.id} style={{
                          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                          borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--background)',
                        }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', overflow: 'hidden',
                            background: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 600, fontSize: '0.75rem', color: 'var(--muted-foreground)',
                          }}>
                            {p.avatarUrl ? (
                              <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              (p.displayName || p.email || '?')[0].toUpperCase()
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.displayName || p.email || 'User'}
                            </div>
                            {p.email && <div style={{ fontSize: '0.68rem', color: 'var(--muted-foreground)' }}>{p.email}</div>}
                          </div>
                          <span style={{
                            padding: '3px 8px', borderRadius: '999px', fontSize: '0.6rem', fontWeight: 600,
                            background: p.permissionLevel === 'edit' ? 'color-mix(in srgb, #3b82f6 15%, transparent)' : 'color-mix(in srgb, var(--muted-foreground) 15%, transparent)',
                            color: p.permissionLevel === 'edit' ? '#3b82f6' : 'var(--muted-foreground)',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>
                            {p.permissionLevel}
                          </span>
                          {isTaskOwner && (
                            <button
                              onClick={() => handleRemoveParticipant(p.userId)}
                              title="Remove participant"
                              style={{
                                padding: '4px', borderRadius: '4px', border: 'none', background: 'transparent',
                                color: '#ef4444', cursor: 'pointer', display: 'flex',
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      ))}

                      {/* Pending invitations */}
                      {invitations.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted-foreground)', padding: '12px 0 4px', marginTop: '8px' }}>
                            Pending Invitations
                          </div>
                          {invitations.map((inv: any) => (
                            <div key={inv.id} style={{
                              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                              borderRadius: '8px', border: '1px dashed var(--border)', background: 'var(--background)', opacity: 0.8,
                            }}>
                              <div style={{
                                width: 32, height: 32, borderRadius: '50%',
                                background: 'color-mix(in srgb, var(--muted-foreground) 15%, transparent)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <Mail size={14} style={{ color: 'var(--muted-foreground)' }} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 500 }}>
                                  {inv.email || 'Open Invite'}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--muted-foreground)' }}>
                                  Expires {new Date(inv.expiresAt).toLocaleDateString()}
                                </div>
                              </div>
                              <span style={{
                                padding: '3px 8px', borderRadius: '999px', fontSize: '0.6rem', fontWeight: 600,
                                background: 'color-mix(in srgb, #f59e0b 15%, transparent)', color: '#f59e0b',
                                textTransform: 'uppercase',
                              }}>
                                Pending
                              </span>
                              <button
                                onClick={() => handleRevokeInvitation(inv.id)}
                                title="Revoke invitation"
                                style={{
                                  padding: '4px', borderRadius: '4px', border: 'none', background: 'transparent',
                                  color: '#ef4444', cursor: 'pointer', display: 'flex',
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
    </>
  );

  if (renderInline) {
    return (
      <div className={styles.inlineShell}>
        <div className={`${styles.modalContent} ${styles.inlineContent}`}>{body}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.modalContent}>{body}</DialogContent>
    </Dialog>
  );
}
